<script lang="ts" setup>
import { priceFormat } from '@/helpers/priceFormat';
import type { ChartData, ChartOptions } from 'chart.js';
import { ArcElement, Chart, Legend, Tooltip } from 'chart.js';
import { computed, ComputedRef } from 'vue';
import { Doughnut } from 'vue-chartjs';
import usePlatformData from './usePlatformData';
import useAssetsData from './useAssetsData';
import { ref } from 'vue';
import BaseValue from '@/components/BaseValue.vue';

const mode = ref<'platform' | 'assets'>('platform');

Chart.register(ArcElement, Tooltip, Legend);

const colors = [
  '#C7F284',
  '#3fc5c1',
  '#2a6041',
  '#4357ad',
  '#6c698d',
  '#eda29d', // red
];

const platformData = usePlatformData();
const assetsData = useAssetsData();

const modeData = computed(() => {
  return mode.value === 'platform' ? platformData : assetsData;
});

const data = computed(() => {
  const labels = modeData.value.dataset.value.map((e) => e.name);
  return {
    labels,
    datasets: [
      {
        data: modeData.value.dataset.value.map((e) => e.value),
        backgroundColor: colors,
      },
    ],
  } satisfies ChartData<'doughnut'>;
});

const options: ComputedRef<ChartOptions<'doughnut'>> = computed(() => ({
  responsive: true,
  cutout: '50%',
  maintainAspectRatio: false,
  color: 'white',
  borderColor: '#18222E',
  plugins: {
    legend: {
      display: false,
    },

    tooltip: {
      callbacks: {
        label: (context) => {
          const pourcentage =
            (Number(context.raw) / modeData.value.total.value) * 100;
          return `${priceFormat(Number(context.raw))} (${pourcentage.toFixed(0)}%)`;
        },
      },
    },
  },
}));
</script>

<template>
  <div class="flex flex-col gap-4 p-4 sm:p-8">
    <div class="flex flex-row items-center gap-8 justify-center">
      <Doughnut
        :data="data"
        :options="options"
        class="max-h-[120px] max-w-[120px] sm:max-h-[180px] sm:max-w-[180px]"
      />
      <ul class="flex flex-col gap-1">
        <li v-for="(item, index) in modeData.dataset.value" :key="item.name">
          <div class="flex flex-row items-center gap-2">
            <div
              class="w-4 h-4 rounded-full shrink-0"
              :style="{ backgroundColor: colors[index] }"
            ></div>
            <p
              class="text-ellipsis text-nowrap flex-1 min-w-0 overflow-hidden max-w-[7ch] sm:max-w-[26ch]"
            >
              {{ item.name }}
              <span class="text-disabled max-sm:hidden"
                >(<BaseValue
                  :value="item.value"
                  :config="{ compact: true }"
                />)</span
              >
            </p>
            {{ item.pourcent.toFixed(2) }}%
          </div>
        </li>
      </ul>
    </div>
    <div class="flex flex-row self-center">
      <v-btn
        @click="mode = 'platform'"
        size="small"
        class="rounded-r-none border-r-surface border-white/10"
        :variant="mode === 'platform' ? 'tonal' : 'outlined'"
      >
        Platforms
      </v-btn>
      <v-btn
        @click="mode = 'assets'"
        size="small"
        class="rounded-l-none border-white/10"
        :variant="mode === 'assets' ? 'tonal' : 'outlined'"
      >
        Assets
      </v-btn>
    </div>
  </div>
</template>
